import { Link, useLocation } from "react-router-dom";
import { Bed, Bath, MapPin, Play, GitCompare } from "lucide-react";
import { resolveImageUrl, getOptimizedImageUrl, getResponsiveSrcSet } from "@/utils/imageUrl";
import { useSiteStyles } from "@/hooks/useSiteStyles";
import ProgressiveImg from "./ProgressiveImg";
import FavouriteButton from "./FavouriteButton";
import ShareButton from "./ShareButton";
import { useCompareCart } from "@/hooks/useCompareCart";
import { toast } from "sonner";

// SellLocalHomes company logo overlay was removed from property images per admin request.

// Transaction status → badge palette. "Available" is intentionally hidden —
// every listed property is available unless explicitly flagged otherwise.
const STATUS_STYLES = {
  "SSTC":         { bg: "bg-amber-500",   text: "text-white", label: "SSTC" },
  "SSTCM":        { bg: "bg-amber-600",   text: "text-white", label: "SSTCM" },
  "Under Offer":  { bg: "bg-purple-600",  text: "text-white", label: "Under Offer" },
  "Reserved":     { bg: "bg-blue-600",    text: "text-white", label: "Reserved" },
  "Let Agreed":   { bg: "bg-teal-600",    text: "text-white", label: "Let Agreed" },
};

const StatusBadge = ({ status, size = "sm" }) => {
  const cfg = STATUS_STYLES[status];
  if (!cfg) return null;
  const px = size === "lg" ? "px-3 py-1 text-xs" : "px-2 py-0.5 text-[10px]";
  return (
    <span
      className={`inline-flex items-center ${px} ${cfg.bg} ${cfg.text} uppercase tracking-wide font-semibold shadow-sm`}
      data-testid={`status-badge-${status.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {cfg.label}
    </span>
  );
};

/**
 * Diagonal "Featured" corner ribbon anchored to the BOTTOM-RIGHT of a
 * relatively-positioned parent (typically the card image). Parent MUST have
 * `overflow-hidden` so the ribbon appears pinned to the corner.
 */
const FeaturedRibbon = ({ size = "md" }) => {
  // ~25% smaller than the original top-right sizes.
  const dims = size === "lg"
    ? { shell: "w-20 h-20", offset: "bottom-[16px] -right-[28px]", px: "px-9 py-0.5", text: "text-[10px]" }
    : { shell: "w-16 h-16", offset: "bottom-[14px] -right-[24px]", px: "px-7 py-0.5", text: "text-[9px]" };
  return (
    <div
      className={`pointer-events-none absolute bottom-0 right-0 ${dims.shell} overflow-hidden z-20`}
      data-testid="featured-ribbon"
      aria-label="Featured property"
    >
      <div
        className={`absolute ${dims.offset} -rotate-45 bg-gradient-to-r from-amber-500 to-amber-600 text-white ${dims.px} ${dims.text} font-semibold uppercase tracking-widest text-center shadow-md`}
      >
        Featured
      </div>
    </div>
  );
};

// Extract YouTube video ID from URL
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/shorts\/([^&\n?#]+)/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// Get YouTube thumbnail URL
const getYouTubeThumbnail = (videoId) => {
  if (!videoId) return null;
  return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
};

export const PropertyCard = ({ property, className = "", span = 4 }) => {
  const { styles } = useSiteStyles();
  const compare = useCompareCart();
  const inCompare = compare.isInCompare(property?.property_id);
  const handleCompareToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const { in: added, full } = compare.toggle(property);
    if (full) {
      toast.error(`Compare tray is full (${compare.max} max) — remove one to add another`);
      return;
    }
    toast.success(added ? "Added to compare" : "Removed from compare");
  };
  const location = useLocation();
  const linkState = { background: location };
  const isFullWidth = span === 12;
  const spanClass = isFullWidth ? "w-full" : span === 6 ? "card-span-6" : span === 8 ? "card-span-8" : "card-span-4";
  const imageHeight = isFullWidth ? "h-56 md:h-64" : span === 4 ? "h-64" : "h-80";

  const cardStyle = {
    backgroundColor: styles.cardBackground || "#e8f0e8",
    borderColor: styles.cardBorder || "#c5d9c5"
  };

  const formatPrice = (price) => {
    const c = property.currency || "£";
    if (!price && price !== 0) return "Price on Application";
    try { return `${c}${Number(price).toLocaleString()}`; } catch { return `${c}${price}`; }
  };

  // Check if property has a video AND show_video_thumbnail is enabled
  const showVideoThumb = property.video_url && property.show_video_thumbnail !== false;
  const videoId = showVideoThumb ? getYouTubeVideoId(property.video_url) : null;
  const thumbnailUrl = videoId ? getYouTubeThumbnail(videoId) : null;
  const displayImage = thumbnailUrl || resolveImageUrl(property.main_image);
  const mainImageUrl = resolveImageUrl(property.main_image);

  // Full-width horizontal card layout
  if (isFullWidth) {
    return (
      <Link
        to={`/property/${property.property_id}`}
        state={linkState}
        className={`property-card block border hover:border-primary/40 hover:shadow-md transition-all duration-300 rounded-sm ${className}`}
        style={cardStyle}
        data-testid={`property-card-${property.property_id}`}
      >
        <div className="flex flex-col md:flex-row">
          {/* Image with Price & Location Overlay */}
          <div
            className="relative w-full md:w-80 h-56 md:h-56 overflow-hidden shrink-0"
            data-testid={`property-card-image-${property.property_id}`}
          >
            <ProgressiveImg
              src={displayImage}
              srcSet={getResponsiveSrcSet(displayImage)}
              sizes="(max-width: 768px) 100vw, 320px"
              alt={property.title}
              loading="lazy"
              className="w-full h-full"
            />
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            
            {/* Agent logo + Share + Favourite — top right, single flex row so they never overlap regardless of logo width */}
            <div className="absolute top-3 right-3 z-20 flex items-center gap-2" data-testid={`favourite-slot-${property.property_id}`}>
              <ShareButton property={property} size="md" />
              <FavouriteButton property={property} />
              {property.agent_logo && (
                <div className="bg-white/90 backdrop-blur-sm p-0.5 rounded">
                  <img
                    src={resolveImageUrl(property.agent_logo)}
                    alt="Agent"
                    className="h-6 w-auto max-w-[60px] object-contain slh-no-img-style"
                  />
                </div>
              )}
            </div>

            {/* Transaction status + Compare toggle — stacked top-left */}
            <div className="absolute top-3 left-3 z-20 flex flex-col items-start gap-2">
              {property.transaction_status && (
                <StatusBadge status={property.transaction_status} />
              )}
              <button
                type="button"
                onClick={handleCompareToggle}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium shadow-md transition-colors ${
                  inCompare
                    ? "bg-primary text-primary-foreground"
                    : "bg-white/90 backdrop-blur-sm text-gray-800 hover:bg-white"
                }`}
                aria-pressed={inCompare}
                data-testid={`compare-toggle-${property.property_id}`}
              >
                <GitCompare className="h-3 w-3" />
                {inCompare ? "In compare" : "Compare"}
              </button>
            </div>

            {/* Featured ribbon - top right corner */}
            {property.featured && <FeaturedRibbon size="md" />}


            {/* Price & Location overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-4">
              {property.price_label && property.price_label !== "Default" && property.price_label !== "Price" && (
                <div
                  className="text-white/85 text-xs uppercase tracking-wider font-medium mb-0.5"
                  data-testid="card-price-label"
                >
                  {property.price_label}
                </div>
              )}
              <div className="text-white font-serif text-xl md:text-2xl font-medium mb-1">
                {formatPrice(property.price)}
              </div>
              <div className="flex items-center gap-1.5 text-white/90 text-sm">
                <MapPin size={14} />
                <span>{property.display_address}</span>
              </div>
            </div>

            {videoId && (
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                  <Play className="w-6 h-6 text-primary ml-1" fill="currentColor" />
                </div>
              </div>
            )}
            {videoId && !property.agent_logo && (
              <span className="absolute top-4 right-4 bg-black/70 text-white text-xs px-2 py-1 flex items-center gap-1">
                <Play size={12} fill="currentColor" />
                Video
              </span>
            )}
          </div>
          
          {/* Content */}
          <div className="flex-1 p-4 md:py-4 md:pr-4 space-y-2">
            <h3 className="font-serif text-xl font-medium text-foreground">
              {property.title}
            </h3>
            
            {/* Beds, Baths, Type grouped together (tenure removed) */}
            <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-muted-foreground text-sm">
              <div className="flex items-center gap-1.5">
                <Bed size={16} />
                <span>{property.beds} Beds</span>
              </div>
              <span className="text-border">•</span>
              <div className="flex items-center gap-1.5">
                <Bath size={16} />
                <span>{property.baths} Baths</span>
              </div>
              {property.property_type && (
                <>
                  <span className="text-border">•</span>
                  <span className="capitalize">{property.property_type}</span>
                </>
              )}
            </div>

            {/* Description preview */}
            <p className="text-muted-foreground line-clamp-2 text-sm leading-relaxed">
              {property.description}
            </p>

            {/* View Details link */}
            <div className="pt-1">
              <span className="text-primary text-sm font-medium hover:underline">
                View Details →
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  // Original grid card layout
  return (
    <Link
      to={`/property/${property.property_id}`}
      state={linkState}
      className={`property-card block ${spanClass} ${className}`}
      data-testid={`property-card-${property.property_id}`}
    >
      <div
        className={`relative ${imageHeight} overflow-hidden`}
        data-testid={`property-card-image-${property.property_id}`}
      >
        <ProgressiveImg
          src={displayImage}
          srcSet={getResponsiveSrcSet(displayImage)}
          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
          alt={property.title}
          loading="lazy"
          className="absolute inset-0 w-full h-full"
          onError={(e) => {
            // Fallback to main image if YouTube thumbnail fails
            if (thumbnailUrl && e.target.src === thumbnailUrl) {
              e.target.src = mainImageUrl;
            }
          }}
        />
        {/* Gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        {/* Agent logo + Share + Favourite — top right, single flex row so they never overlap regardless of logo width */}
        <div className="absolute top-4 right-4 z-20 flex items-center gap-2" data-testid={`favourite-slot-${property.property_id}`}>
          <ShareButton property={property} size="md" />
          <FavouriteButton property={property} />
          {property.agent_logo && (
            <div className="bg-white/90 backdrop-blur-sm p-0.5 rounded">
              <img
                src={resolveImageUrl(property.agent_logo)}
                alt="Agent"
                className="h-6 w-auto max-w-[60px] object-contain slh-no-img-style"
              />
            </div>
          )}
        </div>

        {/* Transaction status + Compare toggle — stacked top-left */}
        <div className="absolute top-4 left-4 z-20 flex flex-col items-start gap-2">
          {property.transaction_status && (
            <StatusBadge status={property.transaction_status} size="lg" />
          )}
          <button
            type="button"
            onClick={handleCompareToggle}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium shadow-md transition-colors ${
              inCompare
                ? "bg-primary text-primary-foreground"
                : "bg-white/90 backdrop-blur-sm text-gray-800 hover:bg-white"
            }`}
            aria-pressed={inCompare}
            data-testid={`compare-toggle-${property.property_id}`}
          >
            <GitCompare className="h-3 w-3" />
            {inCompare ? "In compare" : "Compare"}
          </button>
        </div>

        {/* Featured ribbon - top right corner */}
        {property.featured && <FeaturedRibbon size="lg" />}
        
        {/* Price & Location overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-5">
          {property.price_label && property.price_label !== "Default" && property.price_label !== "Price" && (
            <div
              className="text-white/85 text-xs uppercase tracking-wider font-medium mb-0.5"
              data-testid="card-price-label"
            >
              {property.price_label}
            </div>
          )}
          <div className="text-white font-serif text-2xl font-medium mb-1">
            {formatPrice(property.price)}
          </div>
          <div className="flex items-center gap-1.5 text-white/90 text-sm">
            <MapPin size={14} />
            <span>{property.display_address}</span>
          </div>
        </div>

        {/* Video indicator overlay */}
        {videoId && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
              <Play className="w-6 h-6 text-primary ml-1" fill="currentColor" />
            </div>
          </div>
        )}
        {videoId && !property.agent_logo && (
          <span className="absolute top-4 right-4 bg-black/70 text-white text-xs px-2 py-1 flex items-center gap-1">
            <Play size={12} fill="currentColor" />
            Video
          </span>
        )}
      </div>
      
      <div className="p-5 space-y-2">
        <h3 className="font-serif text-xl font-medium text-foreground line-clamp-1">
          {property.title}
        </h3>
        
        {/* Beds, Baths, Type grouped together (tenure removed) */}
        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-muted-foreground text-sm">
          <div className="flex items-center gap-1">
            <Bed size={14} />
            <span>{property.beds} Beds</span>
          </div>
          <span className="text-border">•</span>
          <div className="flex items-center gap-1">
            <Bath size={14} />
            <span>{property.baths} Baths</span>
          </div>
          {property.property_type && (
            <>
              <span className="text-border">•</span>
              <span className="capitalize">{property.property_type}</span>
            </>
          )}
        </div>
      </div>
    </Link>
  );
};

export default PropertyCard;
